export enum EMarketingChannel {
  WhatsApp = "whatsapp",
  Push = "push"
}

export enum EMarketingConsentStatus {
  Unknown = "unknown",
  OptedIn = "optedIn",
  OptedOut = "optedOut"
}

export enum EMarketingContactSource {
  CtwaAd = "ctwaAd",
  WhatsApp = "whatsapp",
  Order = "order",
  ResellerSignup = "resellerSignup",
  AppInstall = "appInstall"
}

export enum EMarketingContactEventType {
  ProspectEntered = "prospectEntered",
  WhatsappOrderStarted = "whatsappOrderStarted",
  CartCreated = "cartCreated",
  OrderPlaced = "orderPlaced",
  OrderDelivered = "orderDelivered",
  AppOpen = "appOpen",
  ProductView = "productView",
  MessageSent = "messageSent",
  ConsentOptIn = "consentOptIn",
  ConsentOptOut = "consentOptOut",
  ConsentAsked = "consentAsked",
  ConsentDeclined = "consentDeclined"
}

export enum ESegmentAppStatus {
  Installed = "installed",
  NotInstalled = "notInstalled",
  Any = "any"
}

export enum EMarketingTemplateCategory {
  Marketing = "marketing",
  Utility = "utility"
}

export enum EMarketingTemplateStatus {
  Draft = "draft",
  Pending = "pending",
  Approved = "approved",
  Rejected = "rejected"
}

export enum EMarketingCampaignStatus {
  Draft = "draft",
  Running = "running",
  Completed = "completed",
  Failed = "failed"
}

export enum EMarketingFlowTrigger {
  Manual = "manual",
  ResellerSignup = "resellerSignup",
  OrderPlaced = "orderPlaced",
  ProspectEntered = "prospectEntered"
}

export enum EMarketingFlowStatus {
  Active = "active",
  Paused = "paused"
}

export enum EMarketingEnrollmentStatus {
  Active = "active",
  Completed = "completed",
  Stopped = "stopped"
}

export enum EMarketingSkipReason {
  ContactNotFound = "contactNotFound",
  OptedOut = "optedOut",
  ChannelNotAvailable = "channelNotAvailable",
  FrequencyCapped = "frequencyCapped",
  SendFailed = "sendFailed",
  CodRefusalFuseBlown = "codRefusalFuseBlown",
  SaleInProgress = "saleInProgress",
  ConsentRequired = "consentRequired",
  OutsideWindow = "outsideWindow"
}

/** How a message would leave — what Meta bills on. */
export enum EMarketingSendForm {
  Push = "push",
  FreeForm = "freeForm",
  Template = "template",
  Undeliverable = "undeliverable"
}

export interface RecipientPreview {
  contactId: string;
  name: string | null;
  phoneNumber: string;
  channel: EMarketingChannel | null;
  wouldSend: boolean;
  skippedReason: EMarketingSkipReason | null;
}

export interface MarketingContact {
  id: string;
  phoneNumber: string;
  name: string | null;
  source: EMarketingContactSource | null;
  customerId: string | null;
  resellerId: string | null;
  appInstalled: boolean;
  lastOrderAt: string | null;
  lastAppActivityAt: string | null;
  whatsappConsentStatus: EMarketingConsentStatus;
  whatsappConsentUpdatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MarketingContactEvent {
  id: string;
  contactId: string;
  type: EMarketingContactEventType;
  channel: EMarketingChannel | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface GetMarketingContactsParams
  extends Record<string, string | number | undefined> {
  page?: number;
  take?: number;
  search?: string;
}

export interface GetMarketingContactsResponse {
  items: MarketingContact[];
  total: number;
}

export interface GetMarketingContactResponse {
  contact: MarketingContact;
  events: MarketingContactEvent[];
}

export interface UpdateContactConsentPayload {
  phone: string;
  optedIn: boolean;
}

export interface SegmentDefinition {
  appStatus?: ESegmentAppStatus;
  hasOrdered?: boolean;
  minOrders?: number;
  minTotalSpend?: number;
  boughtProductVariantIds?: string[];
  boughtCategoryIds?: string[];
  inactiveForDays?: number;
  excludeOptedOut?: boolean;
}

export interface SegmentPreviewResponse {
  count: number;
}

export interface CampaignEstimateParams {
  segmentDefinition: SegmentDefinition;
  hasText: boolean;
  templateName?: string;
  channel?: EMarketingChannel;
}

export interface CampaignEstimate {
  audience: number;
  deliverable: number;
  billable: number;
  estimatedCostXof: number;
  byForm: Record<EMarketingSendForm, number>;
  bySkipReason: Partial<Record<EMarketingSkipReason, number>>;
  byChannel: Record<EMarketingChannel, number>;
  messageRateXof: number;
}

export interface SegmentResolveResponse {
  count: number;
  contactIds: string[];
}

export interface MarketingTemplate {
  id: string;
  name: string;
  language: string;
  category: EMarketingTemplateCategory;
  status: EMarketingTemplateStatus;
  bodyText: string;
  variableCount: number;
  metaTemplateId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertMarketingTemplatePayload {
  name: string;
  language: string;
  category: EMarketingTemplateCategory;
  bodyText: string;
  variableCount: number;
}

export interface SyncMarketingTemplatesResponse {
  synced: number;
}

export interface MarketingCampaign {
  id: string;
  name: string;
  segmentDefinition: SegmentDefinition;
  messageText: string | null;
  templateName: string | null;
  templateParams: string[] | null;
  channel: EMarketingChannel | null;
  status: EMarketingCampaignStatus;
  sentCount: number;
  skippedCount: number;
  launchedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateMarketingCampaignPayload {
  name: string;
  segmentDefinition: SegmentDefinition;
  messageText?: string;
  templateName?: string;
  templateParams?: string[];
  channel?: EMarketingChannel;
}

export interface UpdateMarketingCampaignPayload {
  campaignId: string;
  data: Partial<CreateMarketingCampaignPayload>;
}

export interface LaunchMarketingCampaignResponse {
  total: number;
  sent: number;
  skipped: number;
}

export interface MarketingFlow {
  id: string;
  name: string;
  trigger: EMarketingFlowTrigger;
  status: EMarketingFlowStatus;
  createdAt: string;
  updatedAt: string;
}

export interface MarketingFlowStep {
  id: string;
  flowId: string;
  position: number;
  delayDays: number;
  channel: EMarketingChannel | null;
  messageText: string | null;
  templateName: string | null;
  templateParams: string[] | null;
}

export interface MarketingFlowEnrollment {
  id: string;
  flowId: string;
  contactId: string;
  position: number;
  nextRunAt: string | null;
  status: EMarketingEnrollmentStatus;
  enrolledAt: string;
  updatedAt: string;
}

export interface GetMarketingFlowResponse {
  flow: MarketingFlow | null;
  steps: MarketingFlowStep[];
  enrollments: MarketingFlowEnrollment[];
}

export interface CreateMarketingFlowPayload {
  name: string;
  trigger: EMarketingFlowTrigger;
}

export interface UpdateMarketingFlowPayload {
  flowId: string;
  data: {
    name?: string;
    trigger?: EMarketingFlowTrigger;
  };
}

export interface UpdateMarketingFlowStatusPayload {
  flowId: string;
  status: EMarketingFlowStatus;
}

export interface MarketingFlowStepData {
  delayDays: number;
  channel?: EMarketingChannel;
  messageText?: string;
  templateName?: string;
  templateParams?: string[];
}

export interface AddMarketingFlowStepPayload {
  flowId: string;
  data: MarketingFlowStepData;
}

export interface UpdateMarketingFlowStepPayload {
  flowId: string;
  stepId: string;
  data: Partial<MarketingFlowStepData>;
}

export interface DeleteMarketingFlowStepPayload {
  flowId: string;
  stepId: string;
}

export interface ReorderMarketingFlowStepsPayload {
  flowId: string;
  stepIds: string[];
}

export interface EnrollMarketingFlowContactPayload {
  flowId: string;
  contactId: string;
}

export interface StopMarketingFlowContactPayload {
  flowId: string;
  contactId: string;
}

export interface StopMarketingFlowContactResponse {
  stopped: boolean;
}

export interface MarketingDispatchPayload {
  contactId: string;
  text?: string;
  templateName?: string;
  templateParams?: string[];
  channel?: EMarketingChannel;
}

export interface MarketingDispatchResponse {
  sent: boolean;
  skippedReason?: EMarketingSkipReason;
  channel?: EMarketingChannel;
}
